import React, { useEffect, useState } from 'react';
import { FilePlus2, Send, Copy, ExternalLink, Trash2, Save, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { OfferItemsEditor } from './OfferItemsEditor';
import { RichTextEditor } from '../../../components/RichTextEditor';
import { PROSPECT_TYPES, fmtDate, money, errMsg } from './crmConstants';

const OFFER_STATUS = {
  draft: { label: 'Bozza', cls: 'bg-slate-600 text-slate-100' },
  sent: { label: 'Inviata', cls: 'bg-violet-500/30 text-violet-200' },
  viewed: { label: 'Aperta dal cliente', cls: 'bg-amber-500/30 text-amber-200' },
  accepted: { label: 'Accettata', cls: 'bg-emerald-500/30 text-emerald-200' },
  declined: { label: 'Rifiutata', cls: 'bg-rose-500/30 text-rose-200' },
};
const publicLink = (o) => `${window.location.origin}/offerta/${o.public_token}`;

const OfferEditor = ({ api, offer, onSaved, showToast }) => {
  const [draft, setDraft] = useState(offer);
  const [busy, setBusy] = useState('');
  useEffect(() => setDraft(offer), [offer]);
  const locked = offer.status === 'accepted';

  const save = async () => {
    setBusy('save');
    try {
      await api.put(`/admin/offers/${offer.id}`, { title: draft.title, intro: draft.intro, items: draft.items, discount_pct: draft.discount_pct, vat_pct: draft.vat_pct, valid_until: draft.valid_until, terms: draft.terms });
      showToast('success', 'Offerta salvata'); onSaved();
    } catch (e) { showToast('error', errMsg(e)); }
    setBusy('');
  };
  const send = async () => {
    if (!window.confirm(`Inviare l'offerta ${offer.number} a ${offer.client_email}?`)) return;
    setBusy('send');
    try {
      await api.put(`/admin/offers/${offer.id}`, { title: draft.title, intro: draft.intro, items: draft.items, discount_pct: draft.discount_pct, vat_pct: draft.vat_pct, valid_until: draft.valid_until, terms: draft.terms });
      await api.post(`/admin/offers/${offer.id}/send`, {});
      showToast('success', 'Offerta inviata al cliente'); onSaved();
    } catch (e) { showToast('error', errMsg(e, 'Invio fallito')); }
    setBusy('');
  };

  return (
    <div className="space-y-3 bg-slate-900/70 border border-slate-700 rounded-xl p-4" data-testid={`offer-editor-${offer.id}`}>
      <input value={draft.title} disabled={locked} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm font-semibold" data-testid="offer-title-input" />
      <div data-testid="offer-intro-input">{locked
        ? <div className="text-sm text-slate-300" dangerouslySetInnerHTML={{ __html: draft.intro }} />
        : <RichTextEditor value={draft.intro} onChange={(html) => setDraft((d) => ({ ...d, intro: html }))} placeholder="Testo introduttivo dell'offerta" minHeight={90} />}</div>
      <OfferItemsEditor value={draft} onChange={(v) => !locked && setDraft(v)} testIdPrefix="offer-items" />
      <div className="grid sm:grid-cols-[auto_1fr] gap-2 items-center text-sm">
        <label className="text-slate-400 text-xs">Valida fino al</label>
        <input type="date" value={draft.valid_until || ''} disabled={locked} onChange={(e) => setDraft({ ...draft, valid_until: e.target.value })} className="px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm w-44" data-testid="offer-valid-input" />
        <label className="text-slate-400 text-xs">Condizioni</label>
        <input value={draft.terms || ''} disabled={locked} onChange={(e) => setDraft({ ...draft, terms: e.target.value })} className="px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm" data-testid="offer-terms-input" />
      </div>
      {!locked && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button size="sm" onClick={save} disabled={!!busy} className="bg-slate-700 hover:bg-slate-600" data-testid="offer-save-btn">{busy === 'save' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Salva</Button>
          <Button size="sm" onClick={send} disabled={!!busy || !offer.client_email} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="offer-send-btn">{busy === 'send' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} {offer.status === 'draft' ? 'Salva e invia al cliente' : 'Reinvia'}</Button>
        </div>
      )}
    </div>
  );
};

export const LeadOffers = ({ api, lead, showToast, onChanged }) => {
  const [offers, setOffers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState('');
  const [openId, setOpenId] = useState(null);
  const load = () => api.get(`/admin/leads/${lead.id}/offers`).then(setOffers).catch(() => {});
  useEffect(() => {
    load();
    api.get('/admin/offer-templates').then((t) => {
      setTemplates(t);
      const match = t.find((x) => x.prospect_type === lead.prospect_type) || t[0];
      if (match) setTplId(match.id);
    }).catch(() => {});
  }, [lead.id]);

  const create = async () => {
    try {
      const o = await api.post(`/admin/leads/${lead.id}/offers`, { template_id: tplId });
      setOpenId(o.id); load(); onChanged();
    } catch (e) { showToast('error', errMsg(e)); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Eliminare l'offerta ${o.number}?`)) return;
    await api.del(`/admin/offers/${o.id}`); load(); onChanged();
  };
  const copy = (o) => { navigator.clipboard?.writeText(publicLink(o)); showToast('success', 'Link copiato'); };

  return (
    <div className="space-y-4" data-testid="lead-offers">
      <div className="flex flex-wrap gap-2 items-center bg-slate-800/60 border border-slate-700 rounded-xl p-3">
        <select value={tplId} onChange={(e) => setTplId(e.target.value)} className="flex-1 min-w-[200px] px-2 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm" data-testid="offer-template-select">
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {PROSPECT_TYPES.find((p) => p.id === t.prospect_type)?.label}</option>)}
        </select>
        <Button size="sm" onClick={create} disabled={!tplId} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="offer-create-btn"><FilePlus2 className="w-4 h-4 mr-1" /> Genera offerta</Button>
      </div>
      {offers.map((o) => {
        const st = OFFER_STATUS[o.status] || OFFER_STATUS.draft;
        return (
          <div key={o.id} className="space-y-2" data-testid={`offer-${o.id}`}>
            <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl border border-slate-700 bg-slate-800/50">
              <button onClick={() => setOpenId(openId === o.id ? null : o.id)} className="flex-1 min-w-0 text-left" data-testid={`offer-toggle-${o.id}`}>
                <p className="text-sm text-white font-medium truncate">{o.number} · {o.title}</p>
                <p className="text-[11px] text-slate-400">{money(o.total)} · creata {fmtDate(o.created_at)}{o.viewed_at ? ` · aperta ${fmtDate(o.viewed_at)}` : ''}{o.accepted_at ? ` · accettata ${fmtDate(o.accepted_at)} da ${o.accepted_name}` : ''}</p>
              </button>
              <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded ${st.cls}`} data-testid={`offer-status-${o.id}`}>{st.label}</span>
              {o.status !== 'draft' && (
                <>
                  <button onClick={() => copy(o)} title="Copia link" className="text-slate-400 hover:text-white" data-testid={`offer-copy-${o.id}`}><Copy className="w-4 h-4" /></button>
                  <a href={publicLink(o)} target="_blank" rel="noopener noreferrer" title="Apri pagina offerta" className="text-slate-400 hover:text-white" data-testid={`offer-open-${o.id}`}><ExternalLink className="w-4 h-4" /></a>
                </>
              )}
              <button onClick={() => remove(o)} className="text-slate-500 hover:text-rose-400" data-testid={`offer-delete-${o.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            {openId === o.id && <OfferEditor api={api} offer={o} showToast={showToast} onSaved={() => { load(); onChanged(); }} />}
          </div>
        );
      })}
      {!offers.length && <p className="text-sm text-slate-500 text-center py-4">Nessuna offerta. Scegli un template e genera la proposta in un clic.</p>}
    </div>
  );
};
